/* eslint-disable no-extra-boolean-cast */
import React, { Fragment, useContext } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';

import { windowWrapper } from '@clubmed/utils';

import { ICONS } from 'Components/utils/icon/icons';
import {
  AFTERNOON_AFTER_5PM,
  AFTERNOON_BEFORE_5PM,
  AFTERNOON_BEFORE_4PM,
  AFTERNOON_AFTER_4PM,
  getAttendeeLabel,
  getSlotKey,
  getAfternoonSlotSubtitle,
  getEveningSlotSubtitle,
  MORNING,
  EVENING,
  AFTERNOON,
  resolveAfternoonSlotType,
  resolveEveningSlotType,
} from 'Components/widgets/reservation-block/reservationBlock.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { ServicesContext } from 'Context/ServicesContext';
import { t } from 'Src/i18n';

import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { Icon } from 'Components/frameworks/icon/Icon';
import { DateSchedule } from 'Components/widgets/reservation-block/DateSchedule';

const slotNotAvailable = {
  isDisabled: true,
  isPaid: false,
  isSelected: false,
};

export const ReservationSlotsStep = ({ showOnlyCurrentAttendeeSlots, attendeeId }) => {
  const appContext = useAppContext();
  const { attendeesList } = useContext(ServicesContext);
  const { values, setFieldValue } = useFormikContext();

  if (!Object.entries(values?.attendees).length || !Object.entries(values.slots).length) {
    return null;
  }

  const attendeeASelectedSchedules = values.attendees.A.schedules.filter(
    ({ isSelected, isPaid }) => isSelected || isPaid,
  );

  const checkAllSlots = ({ target }) => {
    attendeeASelectedSchedules.forEach((schedule) => {
      Object.keys(values.slots).forEach((slotId) => {
        if (
          (target.checked && slotId.endsWith('A')) ||
          (!slotId.includes(schedule.startDate) && !slotId.includes(schedule.endDate))
        ) {
          return;
        }
        const slotIdOfA = slotId.replace(/.$/, 'A');
        windowWrapper.requestAnimationFrame(() => {
          setFieldValue(
            `slots[${slotId}].isSelected`,
            target.checked ? values.slots[slotIdOfA].isSelected : false,
          );
        });
      });
    });
  };
  const filteredAttendees =
    showOnlyCurrentAttendeeSlots && attendeeId
      ? Object.values(values.attendees).filter(({ id }) => id === attendeeId)
      : Object.values(values.attendees);

  const filteredAttendeesArray = [];
  for (const attendee in values.attendees) {
    if (Object.hasOwnProperty.call(values.attendees, attendee)) {
      const element = { attendeeId: attendee, ...values.attendees[attendee] };
      if (showOnlyCurrentAttendeeSlots && attendeeId && attendeeId !== attendee) {
        return;
      }
      filteredAttendeesArray.push(element);
    }
  }
  return (
    <Fragment>
      {filteredAttendees.filter(({ selected }) => selected).length > 1 && (
        <div className="border-b-1 py-15">
          <FormikInput
            label={t('booking.activities.skiEquipment.sameScheduleAllParticipants')}
            name="sameScheduleForAll"
            onChange={checkAllSlots}
            type="checkbox"
          />
        </div>
      )}
      {
        // -expect-error TODO(flow-migration)
        Object.entries(values.attendees).map(
          // -expect-error TODO(flow-migration)
          // eslint-disable-next-line no-shadow
          ([attendeeId, { selected, schedules }]) => {
            if (!selected) {
              return null;
            }

            const attendeeSelectedSchedules = schedules.filter(
              ({ isSelected, isPaid }) => isSelected || isPaid,
            );
            attendeeSelectedSchedules.forEach((schedule) => {
              // ugly fix #97990
              schedule.isSelected = true;
            });

            return (
              <div className="divide-y divide-lightGrey" key={attendeeId}>
                <p className="air360-replay-ignore flex items-center gap-5 py-10 text-14">
                  <Icon name={ICONS.PEOPLE} width="1em" />
                  {getAttendeeLabel(attendeesList, attendeeId, appContext)}
                </p>
                {attendeeSelectedSchedules.map(({ startDate, endDate }, index) => {
                  const { isPaid: isMorningPaid, isDisabled: isMorningDisabled } =
                    values.slots[
                      getSlotKey({ slotName: MORNING, startDate, endDate, attendeeId })
                    ] || slotNotAvailable;

                  // SLOTS BECOMES DYNAMIC
                  // afternoon slot is either before 4pm or before 5pm
                  const afternoonBefore4SlotKey = getSlotKey({
                    slotName: AFTERNOON_BEFORE_4PM,
                    startDate,
                    endDate,
                    attendeeId,
                  });
                  const afternoonBefore5SlotKey = getSlotKey({
                    slotName: AFTERNOON_BEFORE_5PM,
                    startDate,
                    endDate,
                    attendeeId,
                  });
                  const afternoonAfter4SlotKey = getSlotKey({
                    slotName: AFTERNOON_AFTER_4PM,
                    startDate,
                    endDate,
                    attendeeId,
                  });
                  const afternoonAfter5SlotKey = getSlotKey({
                    slotName: AFTERNOON_AFTER_5PM,
                    startDate,
                    endDate,
                    attendeeId,
                  });
                  const eveningSlotKey = getSlotKey({
                    slotName: EVENING,
                    startDate,
                    endDate,
                    attendeeId,
                  });
                  const afternoonSlotKey = getSlotKey({
                    slotName: AFTERNOON,
                    startDate,
                    endDate,
                    attendeeId,
                  });
                  // slots
                  const afternoonBefore4Slot = values.slots[afternoonBefore4SlotKey];
                  const afternoonBefore5Slot = values.slots[afternoonBefore5SlotKey];
                  const afternoonAfter4Slot = values.slots[afternoonAfter4SlotKey];
                  const afternoonAfter5Slot = values.slots[afternoonAfter5SlotKey];

                  const eveningGenericSlot = values.slots[eveningSlotKey];
                  const afternoonGenericSlot = values.slots[afternoonSlotKey];

                  const afternoonSlot =
                    afternoonBefore4Slot ||
                    afternoonBefore5Slot ||
                    afternoonGenericSlot ||
                    slotNotAvailable;
                  const eveningSlot =
                    afternoonAfter4Slot ||
                    afternoonAfter5Slot ||
                    eveningGenericSlot ||
                    slotNotAvailable;

                  const afternoonSlotType = resolveAfternoonSlotType({
                    afternoonBefore4Slot,
                    afternoonBefore5Slot,
                    afternoonGenericSlot,
                  });
                  const eveningSlotType = resolveEveningSlotType({
                    afternoonAfter4Slot,
                    afternoonAfter5Slot,
                    eveningGenericSlot,
                  });

                  const afternoonSlotSubtitle = getAfternoonSlotSubtitle(afternoonSlotType);
                  const eveningSlotSubtitle = getEveningSlotSubtitle(eveningSlotType);

                  const SLOTS = [
                    {
                      isDisabled: isMorningDisabled,
                      isPaid: isMorningPaid,
                      subtitle: 'morning',
                      title: 'morningTitle',
                      type: MORNING,
                    },
                    {
                      isDisabled: afternoonSlot.isDisabled,
                      isPaid: afternoonSlot.isPaid,
                      subtitle: afternoonSlotSubtitle,
                      title: 'afternoonTitle',
                      type: afternoonSlotType,
                    },
                    {
                      isDisabled: eveningSlot.isDisabled,
                      isPaid: eveningSlot.isPaid,
                      subtitle: eveningSlotSubtitle,
                      title: 'eveningTitle',
                      type: eveningSlotType,
                    },
                  ];

                  return (
                    <div className="flex flex-wrap gap-10 py-10" key={index}>
                      <div className="flex flex-col items-stretch rounded-12 border border-saffron bg-saffron px-10 py-5 text-10 font-bold text-black">
                        <DateSchedule date={startDate} />
                        {startDate !== endDate && (
                          <Fragment>
                            <div className="mx-10">{t('common.to')}</div>
                            <DateSchedule date={endDate} />
                          </Fragment>
                        )}
                      </div>
                      {SLOTS.map(({ type, isDisabled, isPaid, title, subtitle }) => {
                        const slotKey = getSlotKey({
                          attendeeId,
                          endDate,
                          slotName: type,
                          startDate,
                        });
                        const attendeeSlotInputName = `slots.${slotKey}.isSelected`;

                        const autoSelectSlotForOtherAttendees = ({ target }) => {
                          windowWrapper.requestAnimationFrame(() => {
                            setFieldValue(attendeeSlotInputName, target.checked);
                          });
                          if (!values.sameScheduleForAll) {
                            return;
                          }

                          Object.keys(values.slots).forEach((slotId) => {
                            if (
                              slotKey === slotId ||
                              !slotId.includes(type + '-' + startDate + '-' + endDate)
                            ) {
                              return;
                            }

                            windowWrapper.requestAnimationFrame(() => {
                              setFieldValue(`slots[${slotId}].isSelected`, target.checked);
                            });
                          });
                        };

                        return (
                          <FormikInput
                            className="grow items-stretch"
                            isDisabled={isDisabled || isPaid}
                            key={attendeeSlotInputName}
                            name={attendeeSlotInputName}
                            onChange={autoSelectSlotForOtherAttendees}
                            // TODO: cleanup !!
                            // eslint-disable-next-line react/no-unstable-nested-components
                            placeholderComponent={(isChecked) => (
                              <div
                                className={classnames(
                                  'flex grow flex-col items-stretch justify-center rounded-12 border-1 px-10 py-5 text-center text-10',
                                  {
                                    'border-lightGrey text-black': !isChecked && !isPaid,
                                    'border-saffron-active bg-saffron text-black':
                                      isChecked || isPaid,
                                    'italic opacity-50': isDisabled || isPaid,
                                  },
                                )}
                              >
                                <div className="font-bold uppercase">
                                  {t(`booking.activities.timeSlot.${title}`)}
                                </div>
                                <div className="text-10">
                                  {t(`booking.activities.timeSlot.subtitles.${subtitle}`)}
                                </div>
                              </div>
                            )}
                            type="checkbox"
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          },
        )
      }
    </Fragment>
  );
};
